// Shared module-level constants, helpers, and types used by both the
// useJvvnlFeederChartData engine hook and the FeederDemand render shell.
// Relocated verbatim from FeederDemand.tsx during the Task 3a refactor.

export const NET_CONSUMPTION_CIRCLE = { value: "KOTPUTLI", label: "KOTPUTLI" };
export const JVVNL_DEMAND_UNITS = ["KW", "KWh", "KVA", "KVAh"] as const;
export const JVVNL_AGG_UNITS = ["MW", "MWh"] as const;
export const JVVNL_GEN_UNITS = ["KW", "KWh", "MW", "MWh"] as const;

export const FUNCTIONS = ["Comparative", "Cumulative"] as const;
export type FunctionMode = (typeof FUNCTIONS)[number];

export const WEATHER_PARAMS = [
  {
    key: "temperature_2m",
    label: "Temperature (°C)",
    chartLabel: "🌡️ Temperature (°C)",
    unit: "°C",
  },
  {
    key: "relative_humidity_2m",
    label: "Humidity (%)",
    chartLabel: "💧 Humidity (%)",
    unit: "%",
  },
  {
    key: "precipitation",
    label: "Precipitation (mm)",
    chartLabel: "🌧️ Precipitation (mm)",
    unit: "mm",
  },
  { key: "rain", label: "Rain (mm)", chartLabel: "🌧️ Rain (mm)", unit: "mm" },
  {
    key: "cloud_cover",
    label: "Cloud Cover (%)",
    chartLabel: "☁️ Cloud Cover (%)",
    unit: "%",
  },
  {
    key: "wind_speed_10m",
    label: "Wind 10m (km/h)",
    chartLabel: "💨 Wind 10m (km/h)",
    unit: "km/h",
  },
  {
    key: "sunshine_duration",
    label: "Sunshine Duration (s)",
    chartLabel: "☀️ Sunshine Duration (s)",
    unit: "s",
  },
  {
    key: "direct_radiation_instant",
    label: "Direct Radiation Instant (W/m²)",
    chartLabel: "🔆 Direct Radiation Instant (W/m²)",
    unit: "W/m²",
  },
];

export const Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];
export type IntervalOption = (typeof Intervals)[number];
export type IntervalKey = IntervalOption["key"];

export const GROUP_BY_OPTIONS = [
  { label: "Circle", rowKey: "group_discom", fallback: "KOTPUTLI" },
  {
    label: "Transco Substation",
    rowKey: "group_substation",
    fallback: "Transco Substation",
  },
  {
    label: "Discom Substation",
    rowKey: "group_discom_substation",
    fallback: "Discom Substation",
  },
  { label: "Feeder Load", rowKey: "group_feeder", fallback: "Feeder Load" },
  { label: "Generation", rowKey: "group_generation", fallback: "Generation" },
  {
    label: "Net Consumption",
    rowKey: "group_net_consumption",
    fallback: "Net Consumption",
  },
] as const;

export type GroupByOption = (typeof GROUP_BY_OPTIONS)[number];

// When all options are selected and the user clicks one (react-select toggles it
// off), isolate to just that single option instead of deselecting it.
export function isolateOnAllSelected<T extends { value: any }>(
  prev: T[],
  next: T[],
  allOptions: T[],
  action?: string,
): T[] {
  // Chip removal / clear must NOT isolate — only menu-deselect (the react-select
  // "deselect-option" / "select-option" path) should collapse-to-one.
  if (
    action === "remove-value" ||
    action === "pop-value" ||
    action === "clear"
  ) {
    return next;
  }
  const selectableCount = allOptions.length;
  if (
    selectableCount > 1 &&
    prev.length === selectableCount &&
    next.length === selectableCount - 1
  ) {
    const removedValue = prev.find(
      (p) => !next.some((n) => n.value === p.value),
    );
    if (removedValue) return [removedValue];
  }
  return next;
}

// Single-pick from an isMany SearchBox: take the newly clicked (last) option,
// not v[0] which is the already-selected first chip.
export function pickLastSingle<T>(v: T | T[] | null | undefined): T | null {
  return Array.isArray(v) ? (v[v.length - 1] ?? null) : (v ?? null);
}

// Single-pick that still honors the collapse-to-one isolate behavior: run the
// raw value through isolateOnAllSelected, prefer the newly added option, else
// the last surviving one.
export function pickSingleWithIsolate<T extends { value: any }>(
  prev: T | null,
  v: T | T[] | null | undefined,
  allOptions: T[],
): T | null {
  const prevArr = prev ? [prev] : [];
  const raw: T[] = Array.isArray(v) ? v : v ? [v] : [];
  const resolved = isolateOnAllSelected(prevArr, raw, allOptions);
  const added = resolved.find((o) => !prevArr.some((p) => p.value === o.value));
  return added ?? resolved[resolved.length - 1] ?? null;
}
